import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpenText, RefreshCw, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ApiError, api, background } from "../../api/client";
import type { Briefing, BriefingSection, BriefingSectionId } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Banner, Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dialog, DialogBody } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Waiting } from "../../components/Waiting";
import { GenerationVeil, jobProgress } from "../../components/GenerationVeil";
import { Presence } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { ViewHeader } from "../../components/ViewHeader";
import { k, t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { licenceStore } from "../../state/licenceScope";
import { useUi } from "../../state/ui";
import { designateDossierReadOnly, useDossierAccess } from "../../sharing/access";
import { clearLocalCopy, leaveWith, readLocalCopy } from "../../state/unsavedDrafts";
import { DossierEmptyState } from "../DossierEmptyState";
import { ExportMenu } from "../order/ExportMenu";
import { TranslationBanner, translationInterval } from "../translation/TranslationBanner";
import { briefingKey, resumeKeyFor, useBriefingJob } from "./briefingJob";
import { writingInProgress } from "./documentJob";
import { AiChip, ProgressStrip, type ProgressTexts } from "./DocumentChrome";
import { type DocNode, PagedBriefing } from "./PagedBriefing";
import { ZoneWaitBanner } from "../../map/zones/ZoneWaitCallout";
import { PendingMissionDraftBanner } from "../PendingMissionDraftBanner";
import { briefingZoneBanner } from "../../map/zones/zoneRefusal";

/** Dossier d'exercice : la « Partie I » de l'écran Dossier de l'iPad. Préambule et situation géopolitique
 *  rédigés par l'IA locale (prompts de l'iPad), situations générale et particulières composées d'après la carte
 *  et l'ordre ; chaque paragraphe se relit, se modifie, se régénère ; le document s'exporte. */

type Lang = "fr" | "en";

const TITLES: Record<Lang, Record<string, string>> = {
  fr: {
    document: "Partie I : Dossier d'exercice",
    part: "1 Mise en ambiance",
    particular: "1.3 Situation particulière",
    bia: "1.3.3 Situation particulière BIA",
    preamble: "Préambule",
    geopolitics: "1.1 Situation géopolitique",
    general: "1.2 Situation générale",
    enemy: "1.3.1 Forces ENI",
    friendly: "1.3.2 Forces AMI",
    bia_situation: "",
    bia_mission: "Mission de la FORAD",
    bia_global: "ENI global",
    bia_initial: "ENI initial",
    bia_ulterieur: "ENI ultérieur",
    bia_synthese: "Synthèse ENI",
  },
  en: {
    document: "Part I: Exercise File",
    part: "1 Route to crisis",
    particular: "1.3 Particular Situation",
    bia: "1.3.3 BIA Particular Situation",
    preamble: "Preamble",
    geopolitics: "1.1 Geopolitical Situation",
    general: "1.2 General Situation",
    enemy: "1.3.1 Enemy Forces",
    friendly: "1.3.2 Friendly Forces",
    bia_situation: "",
    bia_mission: "OPFOR mission",
    bia_global: "Overall enemy",
    bia_initial: "Initial enemy",
    bia_ulterieur: "Subsequent enemy",
    bia_synthese: "Enemy summary",
  },
};

/** Cadre des pages, dans la langue du document : celui du PDF (server/generex_engine/opord/labels.py) ; repris par
 *  la fiche pays. */
export const FRAME: Record<Lang, { banner: string; theme: string }> = {
  // Ligne de tête : le nom de l'exercice seul, comme le titre de l'ordre (demande d'Alexis du 18/10/2026).
  fr: { banner: "CONFIDENTIEL EXERCICE", theme: "{theme}" },
  en: { banner: "EXERCISE CONFIDENTIAL", theme: "{theme}" },
};

const BASE_IDS: BriefingSectionId[] = ["preamble", "geopolitics", "general", "enemy", "friendly"];
const BIA_IDS: BriefingSectionId[] = [
  "bia_situation",
  "bia_mission",
  "bia_global",
  "bia_initial",
  "bia_ulterieur",
  "bia_synthese",
];

/** Aide des formats d'export propre à la mise en ambiance (liste commune : order/ExportMenu.tsx). */
const EXPORT_HINTS = {
  pdf: k("Au cadre des pages de l'OPO, avec lequel il forme le dossier d'exercice"),
  docx: k("Document modifiable, intitulés et couleurs de l'écran"),
};

/** Lien vers les Données initiales, désigné quand le dossier est incomplet (R12). */
const COMPLETE_TARGET = "briefing-complete";

function placeholder(id: BriefingSectionId, lang: Lang): BriefingSection {
  return {
    id,
    title: TITLES[lang][id] ?? "",
    // Toutes les sections sont rédigées par l'IA (demande d'Alexis du 29/09/2026).
    kind: "llm",
    text: "",
    edited: false,
    status: "pending",
    stale: false,
    issues: [],
    model: "",
    generated_at: null,
  };
}

/** Copie locale d'un paragraphe en cours de modification : `generex-briefing-draft:<dossier>:<paragraphe>`. */
const BRIEFING_DRAFT_PREFIX = "generex-briefing-draft:";

export function BriefingView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const setView = useUi((s) => s.setView);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level);
  // Tant que la liste des dossiers n'est pas chargée, on ne sait pas encore : ni bandeau « Dossier incomplet »
  // ni blocage, seulement les actions désactivées.
  const known = Boolean(exercise);
  const incomplete = known && !complete;
  const isCentac = exercise?.parameters.level === "CENTAC";
  const job = useBriefingJob((s) => (s.job && s.job.exerciseId === exerciseId ? s.job : null));
  const query = useQuery({
    queryKey: briefingKey(exerciseId),
    queryFn: () => background(() => api.briefing(exerciseId as string)),
    enabled: Boolean(exerciseId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    // Rédaction en cours ailleurs (autre poste, ou celle d'avant un rechargement qui s'achève) : relue toutes les
    // 10 s, ses paragraphes s'affichent à mesure et une rédaction interrompue est reprise dès son échéance.
    // Préparation d'avance : relue plus souvent, ses paragraphes s'affichent à mesure.
    // Lue dans l'autre langue que sa langue de rédaction (docs/13) : relue toutes les 2,5 s tant qu'une traduction
    // attend.
    refetchInterval: (q) =>
      translationInterval(
        Boolean(q.state.data?.translating?.length),
        q.state.data?.writing && !useBriefingJob.getState().job?.running ? (q.state.data.preparing ? 4_000 : 10_000) : false,
      ),
  });
  const briefing = query.data;
  const start = useBriefingJob((s) => s.start);
  const stop = useBriefingJob((s) => s.stop);
  const dismiss = useBriefingJob((s) => s.dismiss);
  const { data: ai } = useQuery({ queryKey: ["ai-status"], queryFn: () => background(api.aiStatus), refetchInterval: 30_000 });
  // Thème tactique de l'OPO N° 1, sinon le nom du dossier : la ligne de tête des pages, comme dans le PDF.
  const { data: order } = useQuery({
    queryKey: ["order", exerciseId, 1],
    queryFn: () => api.order(exerciseId as string, 1),
    enabled: Boolean(exerciseId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  // Terrain d'une zone libre pas prêt : l'indisponibilité de la mise en ambiance est un refus de zone (« Terrain en
  // préparation… », échec, zone sans terre) ; un seul bandeau d'attente, celui de l'OPO périmé s'il existe.
  const { message: zoneStale, hideUnavailable: zoneUnavailable } = briefingZoneBanner(order?.stale, query.data?.unavailable);
  const themeValue = String(order?.fields?.themeTactique ?? "").trim() || exercise?.name || "";
  const running = Boolean(job?.running);
  const [confirm, setConfirm] = useState<null | { kind: "all" } | { kind: "section"; id: BriefingSectionId }>(null);
  const [editing, setEditing] = useState<BriefingSectionId | null>(null);

  // Rédiger ou régénérer ferme le paragraphe en cours de modification : la question commune d'abord (M4).
  const dossierLocked = useDossierAccess().readOnly;
  const generate = (body: { sections?: BriefingSectionId[] | null; keep_edits?: boolean }) =>
    void leaveWith(() => generateNow(body));
  const generateNow = (body: { sections?: BriefingSectionId[] | null; keep_edits?: boolean }) => {
    if (!exerciseId || running) return;
    // Dossier en lecture seule pour ce compte (docs/17 §3.2) : la rédaction désigne le bandeau de lecture seule.
    if (dossierLocked) {
      designateDossierReadOnly();
      return;
    }
    setConfirm(null);
    setEditing(null);
    if (briefing?.language_mismatch && exercise) {
      // Changement de langue : tout le document est réécrit ; l'ancien texte disparaît tout de suite et chaque
      // paragraphe revient dans la nouvelle langue, sans mélange des deux à l'écran.
      const next: Lang = exercise.language === "en" ? "en" : "fr";
      queryClient.setQueryData<Briefing>(briefingKey(exerciseId), (prev) =>
        prev
          ? {
              ...prev,
              language: next,
              language_mismatch: false,
              enemy_sheet: null,
              sections: prev.sections.map((s) => placeholder(s.id, next)),
            }
          : prev,
      );
    }
    void start(exerciseId, body, queryClient);
  };

  // Langue du dossier changée (Données initiales) : la mise en ambiance est réécrite d'office dans la langue
  // choisie, du titre à la fin (demande d'Alexis du 30/09/2026) ; des paragraphes modifiés à la main
  // attendent la confirmation du bandeau.
  const rewriteKey =
    exerciseId && exercise && complete && !running && briefing?.language_mismatch && !briefing.sections.some((s) => s.edited)
      ? `${exerciseId}:${exercise.language}`
      : null;
  const rewritten = useRef<string | null>(null);
  useEffect(() => {
    if (!rewriteKey || rewritten.current === rewriteKey) return;
    rewritten.current = rewriteKey;
    generate({});
  }, [rewriteKey]);

  // Rédaction interrompue (rechargement, mise en ligne, redémarrage du serveur ; audit m10) : les paragraphes restés
  // vides sont rédigés d'office à la réouverture (« les processus longs se rattrapent seuls ») ; ceux déjà rédigés ou
  // modifiés à la main ne sont jamais réécrits. Un seul poste reprend (le serveur tient la rédaction en cours).
  // Rédaction arrêtée par l'utilisateur sur ce poste : jamais relancée d'office (le serveur le retient aussi).
  const interrupted = briefing?.interrupted ?? [];
  const resumeKey = resumeKeyFor(exerciseId, briefing, { complete, running, stopped: Boolean(job?.stopped) });
  const resumed = useRef<string | null>(null);
  useEffect(() => {
    if (!resumeKey || !exerciseId || resumed.current === resumeKey) return;
    resumed.current = resumeKey;
    notify({
      text: t("Rédaction interrompue : reprise en cours"),
      detail: t("Seuls les paragraphes restés vides sont rédigés ; les autres ne changent pas."),
      tone: "info",
    });
    void start(exerciseId, { sections: interrupted, resume: true }, queryClient);
  }, [resumeKey]);

  // Paragraphe d'une session précédente non enregistré : il se rouvre en modification, une fois par dossier.
  const briefingCopyPrefix = `${BRIEFING_DRAFT_PREFIX}${exerciseId ?? ""}:`;
  const restoredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!briefing || !exerciseId || running || restoredFor.current === briefingCopyPrefix) return;
    restoredFor.current = briefingCopyPrefix;
    for (const key of licenceStore.keys()) {
      if (!key.startsWith(briefingCopyPrefix)) continue;
      const section = briefing.sections.find((s) => s.id === key.slice(briefingCopyPrefix.length));
      const copy = readLocalCopy<string>(key);
      if (!section || !copy || typeof copy.value !== "string" || copy.value.trim() === section.text.trim()) {
        clearLocalCopy(key);
        continue;
      }
      setEditing(section.id);
      break;
    }
  }, [briefing, exerciseId, running, briefingCopyPrefix]);

  const apply = (next: Briefing) => queryClient.setQueryData(briefingKey(exerciseId), next);
  // Échec d'une action : avis en ton d'erreur (les bandeaux sont réservés aux états durables).
  const fail = (err: unknown) =>
    notify({ text: err instanceof Error && err.message ? err.message : t("Opération impossible"), tone: "error" });
  // Dossier incomplet (carte ou niveau manquant) : le lien vers les Données initiales est l'action attendue.
  const completeFirst = () => {
    designate(attentionSelector(COMPLETE_TARGET));
    notify({ text: t("Complétez d'abord les Données initiales : carte et niveau du dossier."), tone: "info" });
  };
  const edit = useMutation({
    mutationFn: ({ id, text }: { id: BriefingSectionId; text: string }) =>
      api.editBriefing(exerciseId as string, briefing?.version ?? 0, { [id]: text }),
    onSuccess: (next) => {
      apply(next);
      setEditing(null);
    },
    onError: async (err) => {
      fail(err);
      await queryClient.invalidateQueries({ queryKey: briefingKey(exerciseId) });
    },
  });
  const reset = useMutation({
    mutationFn: (id: BriefingSectionId) => api.resetBriefingSection(exerciseId as string, id),
    onSuccess: apply,
    onError: fail,
  });

  const lang: Lang = (briefing?.language ?? exercise?.language) === "en" ? "en" : "fr";
  const notFound = query.error instanceof ApiError && query.error.status === 404;
  const languageName = (code: string) => (code === "en" ? t("anglais") : t("français"));

  // Dossier absent, mise en ambiance à rédiger, chargement : en-tête de la vue et état vide dessous (R13).
  if (!exerciseId || (!briefing && !running)) {
    const empty = Boolean(exerciseId) && (notFound || !query.isLoading);
    return (
      <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
        <ViewHeader view="briefing" context={exercise?.name} actions={exerciseId ? <AiChip available={ai?.available} source={ai?.source} resuming={ai?.resuming} /> : null} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!exerciseId ? (
            <DossierEmptyState view="briefing" kind="none">
              {t("Ouvrez ou créez un dossier : la mise en ambiance se rédige à partir de ses Données initiales et de sa carte.")}
            </DossierEmptyState>
          ) : empty ? (
            <BriefingEmpty
              complete={complete}
              known={known}
              centac={isCentac}
              aiAvailable={ai?.available}
              error={job?.error ?? (query.error && !notFound ? (query.error as Error).message : null)}
              onGenerate={() => generate({})}
              onComplete={() => setView("mission")}
              onBlocked={completeFirst}
            />
          ) : (
            <EmptyState variant="loading" title={t("Chargement de la mise en ambiance…")} />
          )}
        </div>
      </div>
    );
  }

  const ids: BriefingSectionId[] = briefing
    ? briefing.sections.map((s) => s.id)
    : [...BASE_IDS, ...(isCentac ? BIA_IDS : [])];
  const sections = Object.fromEntries(
    ids.map((id) => {
      const base = briefing?.sections.find((s) => s.id === id) ?? placeholder(id, lang);
      const fromJob = job?.sections[id];
      return [id, { ...base, ...(fromJob ?? {}), title: base.title || TITLES[lang][id] || "" } as BriefingSection];
    }),
  ) as Record<BriefingSectionId, BriefingSection>;
  const editedCount = ids.filter((id) => sections[id].edited).length;
  const mismatch = Boolean(briefing?.language_mismatch && exercise);
  const staleIds = ids.filter((id) => sections[id].stale);
  const hasBia = ids.some((id) => id.startsWith("bia_"));
  const titles = TITLES[lang];

  // Voile de rédaction : de la demande au dernier paragraphe écrit, sur ce poste ou un autre (rechargement
  // compris).
  const veiled = writingInProgress(job, briefing?.writing);
  const aiIds = ids.filter((id) => sections[id].kind === "llm" && !sections[id].edited);
  const stepLabel = (label: string) => tServer(label.replace(" — rédaction par l'IA", ""));
  const progress = job?.running ? jobProgress(job, aiIds, stepLabel) : null;

  const regenerateAll = () => (editedCount > 0 ? setConfirm({ kind: "all" }) : generate({}));
  const regenerateSection = (id: BriefingSectionId) =>
    sections[id].edited ? setConfirm({ kind: "section", id }) : generate({ sections: [id] });

  // Document dans l'ordre du PDF : titre, préambule, 1 Mise en ambiance (1.1, fiche de l'ennemi, 1.2), 1.3 (ENI,
  // AMI, BIA).
  const nodes: DocNode[] = [
    { type: "title", key: "document", text: titles.document ?? "" },
    { type: "section", id: "preamble", variant: "lead" },
    { type: "heading", key: "part", text: titles.part ?? "", level: "part" },
    { type: "section", id: "geopolitics", variant: "body" },
    ...(briefing?.enemy_sheet ? [{ type: "enemySheet" as const, sheet: briefing.enemy_sheet }] : []),
    { type: "section", id: "general", variant: "body" },
    { type: "heading", key: "particular", text: titles.particular ?? "", level: "section" },
    { type: "section", id: "enemy", variant: "enemy" },
    { type: "section", id: "friendly", variant: "friendly" },
    ...(hasBia
      ? [
          { type: "heading" as const, key: "bia", text: titles.bia ?? "", level: "section" as const },
          ...BIA_IDS.map((id) => ({ type: "section" as const, id, variant: "bia" as const })),
        ]
      : []),
  ];

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="briefing"
        context={exercise?.name}
        badges={
          <>
            {briefing && (
              <Badge title={t("Dossier rédigé en {language}", { language: languageName(briefing.language) })}>
                {briefing.language === "en" ? "EN" : "FR"}
              </Badge>
            )}
            {editedCount > 0 && <Badge tone="accent">{plural(editedCount, t("paragraphe modifié"), t("paragraphes modifiés"))}</Badge>}
          </>
        }
        actions={
          <>
            <AiChip available={ai?.available} source={ai?.source} resuming={ai?.resuming} />
            <Button
              size="sm"
              variant="ghost"
              onClick={regenerateAll}
              disabled={running || !known}
              blocked={incomplete}
              onBlocked={completeFirst}
              title={t("Rédiger à nouveau toute la mise en ambiance")}
              highlight={(mismatch || staleIds.length > 0) && !running && complete}
            >
              <RefreshCw size={14} /> {t("Régénérer")}
            </Button>
            <ExportMenu
              formats={["pdf", "docx", "odt", "txt"]}
              href={(fmt) => api.briefingExportUrl(exerciseId, fmt as "pdf" | "docx" | "odt" | "txt")}
              hints={EXPORT_HINTS}
              disabled={!briefing || running || Boolean(briefing?.translating?.length)}
            />
          </>
        }
      />

      <Presence show={Boolean(job)}>
        {job && <ProgressStrip job={job} texts={progressTexts()} onStop={stop} onRetry={() => generate({})} onDismiss={dismiss} />}
      </Presence>
      <Presence show={Boolean(briefing?.writing) && !job}>
        {briefing?.writing && !job && (
          <Banner tone="info">
            {briefing.preparing ? (
              <Waiting>{t("Generex prépare la mise en ambiance d'avance : les paragraphes s'afficheront dès qu'ils seront prêts…")}</Waiting>
            ) : (
              t("Rédaction en cours : les paragraphes s'afficheront dès qu'ils seront prêts.")
            )}
          </Banner>
        )}
      </Presence>

      <Presence show={incomplete}>
        {incomplete && (
          <Banner
            tone="warn"
            actions={
              <Button size="sm" variant="secondary" data-attention={COMPLETE_TARGET} onClick={() => setView("mission")}>
                {t("Compléter les Données initiales")}
              </Button>
            }
          >
            {t("Dossier incomplet : choisissez la carte et le niveau dans les Données initiales pour rédiger la mise en ambiance.")}
          </Banner>
        )}
      </Presence>
      {/* Zone libre pas encore prête : l'OPO n° 1 (thème des pages, ordre du dossier d'exercice) a été composé sur le
          terrain précédent ; le bouton mène à l'action attendue sur la fiche de la zone. */}
      {/* Mise en ambiance indisponible faute de terrain prêt (même refus que l'ordre) : ce bandeau d'attente, mené à
          l'action attendue de la zone, à la place du bandeau « Données initiales » ; un seul quand l'OPO est aussi
          périmé. */}
      <Presence show={Boolean(zoneStale)}>{zoneStale && <ZoneWaitBanner message={zoneStale} />}</Presence>
      <PendingMissionDraftBanner exercise={exercise} />
      <TranslationBanner
        exerciseId={exerciseId}
        translating={Boolean(briefing?.translating?.length)}
        untranslated={Boolean(briefing?.untranslated?.length)}
      />
      <Presence show={mismatch && Boolean(exercise && briefing)}>
        {mismatch && exercise && briefing && (
          <Banner
            tone="info"
            actions={
              <Button size="sm" onClick={() => generate({})} disabled={running}>
                <RefreshCw size={14} /> {t("Rédiger en {language}", { language: languageName(exercise.language) })}
              </Button>
            }
          >
            {t("Cette mise en ambiance est rédigée en {from} ; le dossier est désormais en {to}.", {
              from: languageName(briefing.language),
              to: languageName(exercise.language),
            })}
            {/* Réécriture d'office, sauf quand des paragraphes ont été modifiés à la main : ils seraient perdus. */}
            {editedCount > 0 && ` ${t("Vos paragraphes modifiés seront réécrits eux aussi.")}`}
          </Banner>
        )}
      </Presence>
      <Presence show={Boolean(briefing?.unavailable) && !zoneUnavailable}>
        {briefing?.unavailable && !zoneUnavailable && (
          <Banner
            tone="warn"
            actions={
              <Button size="sm" variant="secondary" onClick={() => setView("mission")}>
                {t("Données initiales")}
              </Button>
            }
          >
            {tServer(briefing.unavailable)} {t("Les paragraphes restent tels qu'ils ont été rédigés.")}
          </Banner>
        )}
      </Presence>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <GenerationVeil
          active={veiled}
          progress={progress}
          count={(done, total) => t("Paragraphes rédigés : {done} sur {total}", { done, total })}
          note={t("Les paragraphes s'afficheront dès qu'ils seront prêts.")}
        >
          <PagedBriefing
            lang={lang}
            nodes={nodes}
            sections={sections}
            job={job}
            editing={editing}
            savingId={edit.isPending ? (edit.variables?.id ?? null) : null}
            busy={running}
            banner={FRAME[lang].banner}
            themeLine={FRAME[lang].theme.replace("{theme}", themeValue)}
            copyPrefix={briefingCopyPrefix}
            onEdit={(id) => id !== editing && leaveWith(() => setEditing(id))}
            onCancelEdit={() => setEditing(null)}
            onSave={(id, text) => edit.mutateAsync({ id, text })}
            onRegenerate={regenerateSection}
            onReset={(id) => reset.mutate(id)}
            onCopied={() => notify({ text: t("Paragraphe copié dans le presse-papiers."), tone: "ok" })}
          />
        </GenerationVeil>
      </div>

      <Presence show={confirm?.kind === "all"}>
        {confirm?.kind === "all" && (
          <Dialog
            title={t("Régénérer la mise en ambiance ?")}
            onClose={() => setConfirm(null)}
            footer={
              <>
                <Button variant="secondary" onClick={() => setConfirm(null)}>{t("Annuler")}</Button>
                <Button variant="secondary" onClick={() => generate({ keep_edits: true })}>{t("Conserver mes modifications")}</Button>
                <Button variant="danger" onClick={() => generate({ keep_edits: false })}>{t("Tout régénérer")}</Button>
              </>
            }
          >
            <DialogBody>
              <p className="text-ui leading-relaxed text-ink-soft">
                {t(
                  "Vous avez modifié {count}. « Conserver mes modifications » rédige à nouveau le reste de la mise en ambiance ; « Tout régénérer » remplace aussi vos modifications.",
                  { count: plural(editedCount, t("paragraphe"), t("paragraphes")) },
                )}
              </p>
            </DialogBody>
          </Dialog>
        )}
      </Presence>
      <Presence show={confirm?.kind === "section"}>
        {confirm?.kind === "section" && (
          <ConfirmDialog
            title={t("Régénérer ce paragraphe ?")}
            confirmLabel={t("Régénérer")}
            busy={running}
            busyLabel={t("Régénération…")}
            onCancel={() => setConfirm(null)}
            onConfirm={() => generate({ sections: [confirm.id] })}
          >
            {t("Le paragraphe « {title} » a été modifié à la main : sa nouvelle rédaction remplacera vos modifications.", {
              title: sections[confirm.id].title || titles.preamble,
            })}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}

/** Textes du bandeau de la rédaction de la mise en ambiance. */
function progressTexts(): ProgressTexts {
  return {
    one: (duration) => t("Paragraphe rédigé en {duration}", { duration }),
    some: (duration) => t("Paragraphes rédigés en {duration}", { duration }),
    all: (duration) => t("Dossier rédigé en {duration}", { duration }),
    stopped: t("Rédaction arrêtée : les paragraphes terminés sont enregistrés."),
  };
}

/** Mise en ambiance à rédiger : état vide de la vue (R13), avec ce que l'IA va rédiger et une seule action
 *  principale. Dossier incomplet : « Rédiger le dossier » désigne le lien vers les Données initiales. */
function BriefingEmpty({
  complete,
  known,
  centac,
  aiAvailable,
  error,
  onGenerate,
  onComplete,
  onBlocked,
}: {
  complete: boolean;
  /** Dossier chargé (sinon l'état « incomplet » n'est pas encore connu). */
  known: boolean;
  centac: boolean;
  aiAvailable?: boolean;
  error: string | null;
  onGenerate: () => void;
  onComplete: () => void;
  onBlocked: () => void;
}) {
  const items: { title: string; text: string }[] = [
    {
      title: t("Préambule et situation géopolitique"),
      text: t("Rédigés par l'IA locale d'après les consignes de rédaction Generex."),
    },
    {
      title: t("Situations générale et particulières"),
      text: t("Rédigées par l'IA d'après la carte et l'ordre : mêmes localités, appuis et dates que l'OPO."),
    },
    ...(centac
      ? [
          {
            title: t("Situation particulière BIA"),
            text: t("Rédigée par l'IA d'après le contexte de la rotation face à la FORAD, jusqu'à la synthèse ENI."),
          },
        ]
      : []),
  ];
  return (
    <EmptyState
      icon={BookOpenText}
      title={t("Mise en ambiance")}
      action={
        <Button
          data-attention="briefing-write"
          highlight={complete}
          disabled={!known}
          blocked={known && !complete}
          onBlocked={onBlocked}
          onClick={onGenerate}
        >
          <Sparkles size={14} /> {t("Rédiger le dossier")}
        </Button>
      }
      secondary={
        known && !complete && (
          <Button variant="link" data-attention={COMPLETE_TARGET} onClick={onComplete}>
            {t("Compléter les Données initiales")}
          </Button>
        )
      }
      note={
        aiAvailable === false
          ? t("IA hors ligne : texte source repris pour ses paragraphes.")
          : centac
            ? t("Environ deux minutes, suivies en direct.")
            : t("Environ une minute, suivie en direct.")
      }
    >
      <p>{t("Partie I du dossier d'exercice, que complète l'OPO")}</p>
      {/* Liste alignée à gauche dans un encadré : un bloc ancré au milieu de l'état vide centré. */}
      <ul className="mt-4 space-y-3 rounded-lg border border-line bg-canvas/60 px-4 py-3 text-left">
        {items.map((it) => (
          <li key={it.title} className="flex gap-2.5">
            <Sparkles size={14} className="mt-0.5 shrink-0 text-run" aria-hidden />
            <span>
              <span className="block leading-snug font-medium text-ink">{it.title}</span>
              <span className="block text-meta leading-snug text-ink-soft">{it.text}</span>
            </span>
          </li>
        ))}
      </ul>
      <Presence show={Boolean(error)}>
        {error && (
          <Callout tone="danger" className="mt-4 text-left">
            {tServer(error)}
          </Callout>
        )}
      </Presence>
    </EmptyState>
  );
}
